import React, { useState } from 'react'
import { useCookies } from 'react-cookie'

import { STACK, VIEW, FORMAT } from 'types/deck'
import { SET_CACHEABLES_STATE } from 'app-redux/cacheables/actions/types'

import { useRightPanel } from 'hooks/usePinnedSidecar'
import { useAppSelector, useActionless } from 'app-redux/hooks'

import PhatButton from 'components/formElements/PhatButton'
import ContextMenu from 'components/elements/ContextMenu'
import ToastService from 'services/toast.service'
import Icon from 'components/elements/Icon'
import ConfirmDeleteModal from 'components/elements/ConfirmDeleteModal'

import {
  categoriesCanCollapse,
  collapsibleCategoryCookieKey,
  getCollapsedCategoryKey,
} from 'hooks/collapsibleCategories'
import { usePinnedInteractions } from 'hooks/usePinnedSidecar'

import styles from './viewExtras.module.scss'

const ALLOW_CARD_UI_TWEAK_KEY = 'tb-accpet-card-ui-tweaks'

type Props = {}

const ViewExtras = ({}: Props) => {
  const imageSize = useAppSelector(state => state.cacheables.imageSize)
  const disableMargins = useAppSelector(state => state.cacheables.disableMargins)
  const showEmptyCardStacks = useAppSelector(state => state.cacheables.showEmptyCardStacks)
  const hideDefaultTypeCategories = useAppSelector(state => state.cacheables.hideDefaultTypeCategories)
  const textColumns = useAppSelector(state => state.cacheables.textColumns)
  const imageCardLayers = useAppSelector(state => state.cacheables.imageCardLayers)
  const pinnedCategory = useAppSelector(state => state.cacheables.pinnedCategory)
  const draggableStacks = useAppSelector(state => state.cacheables.draggableStacks)
  const hideOutOfDeckCategories = useAppSelector(state => state.cacheables.hideOutOfDeckCategories)
  const view = useAppSelector(state => state.deck.view)
  const stack = useAppSelector(state => state.deck.stack)
  const deckFormat = useAppSelector(state => state.deck.format)
  const categories = useAppSelector(state => state.deck.categories)
  const deckId = useAppSelector(state => state.deck.id)
  const disableDeckCursorCard = useAppSelector(state => state.cacheables.disableDeckCursorCard)
  const isMidsize = useAppSelector(state => state.active.isMidsize)
  const staticCard = useAppSelector(state => state.cacheables.staticCard)

  const [rightPanelStats, setRightPanelStatus] = useRightPanel()
  const [pinnedInteractionStatus, setPinnedInteractionStatus] = usePinnedInteractions()

  const [acceptUiTweaksTerms, setAcceptUiTweaksTerms] = useState(false)

  const [{ tbCollapsedCategories, [ALLOW_CARD_UI_TWEAK_KEY]: allowCardUiTweaks }, setCookies] = useCookies([
    collapsibleCategoryCookieKey,
    ALLOW_CARD_UI_TWEAK_KEY,
  ])
  const canCollapse = categoriesCanCollapse(stack)

  const handleUpdateTextColumns = (newState: Partial<typeof textColumns>) => {
    setCacheableState({ textColumns: { ...textColumns, ...newState } })
  }

  const handleUpdateImageLayers = (newState: Partial<typeof imageCardLayers>) => {
    if (allowCardUiTweaks !== 'true') return setAcceptUiTweaksTerms(true)

    setCacheableState({ imageCardLayers: { ...imageCardLayers, ...newState } })
  }

  const handleUpdateAllImageLayers = (state: boolean) => {
    if (allowCardUiTweaks !== 'true') return setAcceptUiTweaksTerms(true)

    setCacheableState({
      imageCardLayers: {
        foilLayer: state,
        qtyAndColorTag: state,
        qtyControls: state,
        openDetailsButton: state,
        cardImage: state,
        collectionRibbon: state,
        errorBorder: state,
        gameChangerIndicator: state,
        flipButton: state,
        canlanderPoints: state,
        customCardIndicator: state,
        secondaryCategoryIndicator: state,
        pinnedCardStatus: state,
      },
    })
  }

  const [setCacheableState] = useActionless(SET_CACHEABLES_STATE)

  const collapseExpandAllCategories = (action: 'expand' | 'collapse') => {
    const newCollapsedCategories: Record<string, boolean> = Object.keys(categories).reduce(
      (acc, category) => {
        const categoryAndDeckKey = getCollapsedCategoryKey(category, deckId)
        acc[categoryAndDeckKey] = action === 'collapse' ? true : false
        return acc
      },
      {} as Record<string, boolean>,
    )

    setCookies(collapsibleCategoryCookieKey, { ...tbCollapsedCategories, ...newCollapsedCategories }, { path: '/' })
  }

  return (
    <>
      <ContextMenu
        openOnLeftClick
        menuClassName={styles.menu}
        options={[
          {
            type: 'extras',
            id: 'card-size-picker',
            label: 'Card image size',
            icon: 'image outline',
            hidden: view !== VIEW.CARD && view !== VIEW.GRID,
            options: [
              {
                label: 'Small',
                icon: imageSize === 1 ? 'check circle outline' : 'circle outline',
                onClick: () => setCacheableState({ imageSize: 1 }),
              },
              {
                label: 'Normal',
                icon: imageSize === 2 ? 'check circle outline' : 'circle outline',
                onClick: () => setCacheableState({ imageSize: 2 }),
              },
              {
                label: 'Large',
                icon: imageSize === 3 ? 'check circle outline' : 'circle outline',
                onClick: () => setCacheableState({ imageSize: 3 }),
              },
            ],
          },
          {
            type: 'extras',
            id: 'card-layer-picker',
            label: 'Card UI elements',
            icon: 'address card outline',
            hidden: view !== VIEW.CARD && view !== VIEW.GRID,
            className: styles.imageLayerMenu,
            options: [
              {
                icon: 'check circle outline',
                label: 'Reset to default',
                onClick: () => handleUpdateAllImageLayers(true),
                noCloseOnClick: true,
              },
              {
                icon: 'circle outline',
                label: 'Disable all',
                onClick: () => handleUpdateAllImageLayers(false),
                noCloseOnClick: true,
              },
              { type: 'spacer' },

              {
                type: 'checkbox',
                checked: imageCardLayers.qtyAndColorTag,
                label: 'Quantity & Color tag',
                onChange: () => handleUpdateImageLayers({ qtyAndColorTag: !imageCardLayers.qtyAndColorTag }),
              },
              {
                type: 'checkbox',
                checked: imageCardLayers.qtyControls,
                label: 'Quantity controls (+/-)',
                onChange: () => handleUpdateImageLayers({ qtyControls: !imageCardLayers.qtyControls }),
              },
              {
                type: 'checkbox',
                checked: imageCardLayers.openDetailsButton,
                label: 'Card details button',
                onChange: () => handleUpdateImageLayers({ openDetailsButton: !imageCardLayers.openDetailsButton }),
              },
              {
                type: 'checkbox',
                checked: imageCardLayers.flipButton,
                label: 'Flip card button',
                onChange: () => handleUpdateImageLayers({ flipButton: !imageCardLayers.flipButton }),
              },
              {
                type: 'checkbox',
                checked: imageCardLayers.pinnedCardStatus,
                label: 'Pinned card status',
                onChange: () => handleUpdateImageLayers({ pinnedCardStatus: !imageCardLayers.pinnedCardStatus }),
              },
              { type: 'spacer' },
              {
                type: 'checkbox',
                checked: imageCardLayers.collectionRibbon,
                label: 'Collection status ribbon',
                onChange: () => handleUpdateImageLayers({ collectionRibbon: !imageCardLayers.collectionRibbon }),
              },
              {
                type: 'checkbox',
                checked: imageCardLayers.gameChangerIndicator,
                label: 'Game changer indicator',
                onChange: () =>
                  handleUpdateImageLayers({ gameChangerIndicator: !imageCardLayers.gameChangerIndicator }),
              },
              {
                type: 'checkbox',
                checked: imageCardLayers.canlanderPoints,
                label: 'Canlander points',
                onChange: () => handleUpdateImageLayers({ canlanderPoints: !imageCardLayers.canlanderPoints }),
              },
              {
                type: 'checkbox',
                checked: imageCardLayers.customCardIndicator,
                label: 'Custom card indicator',
                onChange: () => handleUpdateImageLayers({ customCardIndicator: !imageCardLayers.customCardIndicator }),
              },
              { type: 'spacer' },
              {
                type: 'checkbox',
                checked: imageCardLayers.foilLayer,
                label: 'Foil layer',
                onChange: () => handleUpdateImageLayers({ foilLayer: !imageCardLayers.foilLayer }),
              },
              {
                type: 'checkbox',
                checked: imageCardLayers.errorBorder,
                label: 'Card error border',
                onChange: () => handleUpdateImageLayers({ errorBorder: !imageCardLayers.errorBorder }),
              },
              {
                type: 'checkbox',
                checked: imageCardLayers.cardImage,
                label: 'Card image',
                onChange: () => handleUpdateImageLayers({ cardImage: !imageCardLayers.cardImage }),
              },
              {
                type: 'checkbox',
                checked: imageCardLayers.secondaryCategoryIndicator,
                label: 'Multiple category layer',
                onChange: () =>
                  handleUpdateImageLayers({ secondaryCategoryIndicator: !imageCardLayers.secondaryCategoryIndicator }),
              },
            ],
          },
          {
            label: 'Enabled columns',
            icon: 'columns',
            type: 'extras',
            id: 'text-view-columns',
            hidden: view !== VIEW.TEXT,
            options: [
              {
                type: 'checkbox',
                checked: textColumns.extraOptions,
                label: 'Extra Options',
                onChange: () => handleUpdateTextColumns({ extraOptions: !textColumns.extraOptions }),
              },
              {
                type: 'checkbox',
                checked: textColumns.dragHandle,
                label: 'Drag Handle',
                onChange: () => handleUpdateTextColumns({ dragHandle: !textColumns.dragHandle }),
              },
              {
                type: 'checkbox',
                checked: textColumns.finishes,
                label: 'Finishes',
                onChange: () => handleUpdateTextColumns({ finishes: !textColumns.finishes }),
              },
              {
                type: 'checkbox',
                checked: textColumns.manaCost,
                label: 'Mana Cost',
                onChange: () => handleUpdateTextColumns({ manaCost: !textColumns.manaCost }),
              },
              {
                type: 'checkbox',
                checked: textColumns.setSymbol,
                label: 'Set Symbol',
                onChange: () => handleUpdateTextColumns({ setSymbol: !textColumns.setSymbol }),
              },
              {
                type: 'checkbox',
                checked: textColumns.price,
                label: 'Price',
                onChange: () => handleUpdateTextColumns({ price: !textColumns.price }),
              },
              {
                type: 'checkbox',
                checked: textColumns.collectionInfo,
                label: 'Collection info',
                onChange: () => handleUpdateTextColumns({ collectionInfo: !textColumns.collectionInfo }),
              },

              {
                type: 'checkbox',
                checked: textColumns.colorTag,
                label: 'Color tag',
                onChange: () => handleUpdateTextColumns({ colorTag: !textColumns.colorTag }),
              },
              {
                type: 'checkbox',
                checked: textColumns.pinnedInfo,
                label: 'Pinned status',
                onChange: () => handleUpdateTextColumns({ pinnedInfo: !textColumns.pinnedInfo }),
              },
              {
                hidden: deckFormat !== FORMAT.EDH,
                type: 'checkbox',
                checked: textColumns.gamechangers,
                label: 'Game Changer',
                onChange: () => handleUpdateTextColumns({ gamechangers: !textColumns.gamechangers }),
              },
              {
                hidden: deckFormat !== FORMAT.CANLANDER,
                type: 'checkbox',
                checked: textColumns.canlanderPoints,
                label: 'Canlander Points',
                onChange: () => handleUpdateTextColumns({ canlanderPoints: !textColumns.canlanderPoints }),
              },
            ],
          },
          {
            label: 'Disable hover card',
            hidden: view !== VIEW.TEXT && view !== VIEW.SPREADSHEET,
            checked: disableDeckCursorCard,
            type: 'checkbox',
            onChange: () => setCacheableState({ disableDeckCursorCard: !disableDeckCursorCard }),
          },
          { type: 'spacer', hidden: view === VIEW.LIST },
          {
            label: 'Disable margins',
            type: 'checkbox',
            checked: disableMargins,
            hidden: view === VIEW.SPREADSHEET || isMidsize,
            onChange: () => {
              setCacheableState({ disableMargins: !disableMargins })

              if (window.innerWidth < 2300 && !disableMargins)
                ToastService.create(
                  'This option does nothing for screens less than 2300 pixels wide',
                  'Deck options',
                  'warning',
                )
            },
          },
          {
            label: 'Draggable categories',
            type: 'checkbox',
            checked: draggableStacks,
            onChange: () => setCacheableState({ draggableStacks: !draggableStacks }),
            hidden: view !== VIEW.CARD && view !== VIEW.TEXT,
            disabled: stack !== STACK.MULTIPLE && stack !== STACK.CUSTOM,
          },
          {
            label: 'Static card',
            type: 'checkbox',
            checked: staticCard,
            hidden: isMidsize,
            onChange: () => setCacheableState({ staticCard: !staticCard }),
          },
          {
            label: 'Pinned deck stats',
            type: 'checkbox',
            hidden: isMidsize,
            checked: rightPanelStats.locked && rightPanelStats.open,
            onChange: () =>
              setRightPanelStatus({ ...rightPanelStats, locked: !rightPanelStats.locked, open: !rightPanelStats.open }),
          },
          {
            label: 'Pinned description',
            type: 'checkbox',
            hidden: isMidsize,
            checked: pinnedInteractionStatus.locked,
            onChange: () =>
              setPinnedInteractionStatus({ ...pinnedInteractionStatus, locked: !pinnedInteractionStatus.locked }),
          },
          {
            label: 'Pinned category',
            type: 'checkbox',
            checked: pinnedCategory,
            onChange: () => setCacheableState({ pinnedCategory: !pinnedCategory }),
          },
          { type: 'spacer' },
          {
            type: 'extras',
            id: 'empty-category-options',
            label: 'Category view options',
            options: [
              {
                label: 'Show empty categories',
                type: 'checkbox',
                checked: showEmptyCardStacks,
                onChange: () => setCacheableState({ showEmptyCardStacks: !showEmptyCardStacks }),
              },
              {
                label: 'Hide default categories',
                type: 'checkbox',
                checked: hideDefaultTypeCategories,
                disabled: !showEmptyCardStacks,
                onChange: () => setCacheableState({ hideDefaultTypeCategories: !hideDefaultTypeCategories }),
              },
              {
                label: 'Hide Maybeboard(s)',
                type: 'checkbox',
                checked: hideOutOfDeckCategories,
                onChange: () => setCacheableState({ hideOutOfDeckCategories: !hideOutOfDeckCategories }),
              },
              { type: 'spacer' },
              {
                label: 'Collapse all categories',
                icon: 'angle double up',
                disabled: !canCollapse,
                onClick: () => collapseExpandAllCategories('collapse'),
              },
              {
                label: 'Expand all categories',
                icon: 'angle double down',
                disabled: !canCollapse,
                onClick: () => collapseExpandAllCategories('expand'),
              },
            ],
          },
        ]}>
        <PhatButton className={styles.trigger}>
          <Icon name="cog" />
        </PhatButton>
      </ContextMenu>

      <ConfirmDeleteModal
        header="Allow card UI tweaks"
        delayEnabledTime={3000}
        message={
          <div className={styles.confirmUiTweakMessage}>
            <p>
              Once enabled, you'll be able to manually enable / disable all UI elements that are placed on cards. Know
              that disabling UI elements may result in making the UI less clear or hide the clearest option for certain
              actions.
            </p>

            <p>
              Even with all elements disabled, all actions and information are still accessible though other means. If
              you feel like actions are missing, here are some tips:
            </p>

            <ul>
              <li>Use the three dot menu below the card for quick tweaks</li>
              <li>Right clicking on a card opens the same menu as the three dot menu below cards</li>
              <li>Double clicking a card opens that card's details overlay</li>
              <li>Check the keybinds overlay for more options</li>
            </ul>
          </div>
        }
        buttonText="Allow UI tweaking"
        open={acceptUiTweaksTerms}
        onClose={() => setAcceptUiTweaksTerms(false)}
        onConfirm={() => {
          setCookies(ALLOW_CARD_UI_TWEAK_KEY, true, {
            path: '/',
            expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365 * 10), // 10 years
          })

          setAcceptUiTweaksTerms(false)
        }}
      />
    </>
  )
}

export default ViewExtras
